import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchRisks } from '../api';
import { Badge, Empty, ErrorBox, Loading, RelativeTime, Section, Table } from '../components/ui';

const IMPACT_ORDER = ['critical', 'high', 'medium', 'low'] as const;
const PROBABILITY_ORDER = ['high', 'medium', 'low'] as const;

const IMPACT_LABEL: Record<string, string> = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Medio',
  low: 'Bajo',
};
const PROBABILITY_LABEL: Record<string, string> = { high: 'Alta', medium: 'Media', low: 'Baja' };
const STATUS_LABEL: Record<string, string> = {
  open: 'Abierto',
  mitigated: 'Mitigado',
  accepted: 'Aceptado',
  closed: 'Cerrado',
};

export function RisksPage({ projectSlug }: { projectSlug?: string }): JSX.Element {
  const query = useQuery({
    queryKey: ['risks', projectSlug],
    queryFn: () => fetchRisks(projectSlug),
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { risks, blockers } = query.data;
  const open = risks.filter((risk) => risk.status === 'open');
  const openBlockers = blockers.filter((blocker) => blocker.resolvedAt === null);

  return (
    <>
      {!projectSlug && (
        <>
          <h1 className="mb-1 text-lg font-semibold">Riesgos y bloqueos</h1>
          <p className="mb-5 text-[12px] text-[var(--color-ink-muted)]">
            {open.length} abierto(s) de {risks.length}, y {openBlockers.length} bloqueo(s) sin
            resolver. Los códigos R1–R24 vienen de la auditoría inicial.
          </p>
        </>
      )}

      {/* ── Matriz probabilidad × impacto ─────────────────────────────────── */}
      <Section
        title="Matriz de riesgo"
        subtitle="Solo los abiertos. La celda de arriba a la izquierda es la que hay que vaciar primero"
      >
        {open.length === 0 ? (
          <Empty>Ningún riesgo abierto.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="border-collapse text-[12px]">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                    Probabilidad ↓ / Impacto →
                  </th>
                  {IMPACT_ORDER.map((impact) => (
                    <th
                      key={impact}
                      className="min-w-[110px] px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]"
                    >
                      {IMPACT_LABEL[impact]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PROBABILITY_ORDER.map((probability) => (
                  <tr key={probability}>
                    <th className="whitespace-nowrap px-2 py-1.5 text-left text-[11px] font-medium text-[var(--color-ink-muted)]">
                      {PROBABILITY_LABEL[probability]}
                    </th>
                    {IMPACT_ORDER.map((impact) => {
                      const cell = open.filter(
                        (risk) => risk.probability === probability && risk.impact === impact,
                      );
                      // Rojo solo donde de verdad duele: alta probabilidad con
                      // impacto crítico o alto.
                      const severe =
                        (probability === 'high' && (impact === 'critical' || impact === 'high')) ||
                        (probability === 'medium' && impact === 'critical');
                      return (
                        <td
                          key={impact}
                          className={`border border-[var(--color-border-subtle)] px-2 py-1.5 align-top ${
                            cell.length === 0
                              ? ''
                              : severe
                                ? 'bg-red-50 dark:bg-red-950/30'
                                : 'bg-amber-50 dark:bg-amber-950/20'
                          }`}
                        >
                          {cell.length === 0 ? (
                            <span className="text-[var(--color-ink-muted)]">·</span>
                          ) : (
                            <ul className="space-y-0.5">
                              {cell.map((risk) => (
                                <li key={risk.id} className="text-[11px]" title={risk.title}>
                                  <strong className="font-semibold">{risk.code ?? '—'}</strong>{' '}
                                  {risk.title.slice(0, 26)}
                                  {risk.title.length > 26 ? '…' : ''}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Riesgos" subtitle={`${risks.length} registrados`}>
        <Table head={['Cód.', 'Riesgo', 'Proyecto', 'Prob.', 'Impacto', 'Estado', 'Mitigación']}>
          {risks.map((risk) => (
            <tr
              key={risk.id}
              className={`border-b border-[var(--color-border-subtle)] align-top last:border-0 ${
                risk.status !== 'open' ? 'opacity-55' : ''
              }`}
            >
              <td className="px-3 py-2 font-mono text-[11px]">{risk.code ?? '—'}</td>
              <td className="px-3 py-2">
                <span className="font-medium">{risk.title}</span>
                {risk.description && (
                  <p className="mt-0.5 text-[11px] leading-snug text-[var(--color-ink-muted)]">
                    {risk.description}
                  </p>
                )}
                {risk.evidence && (
                  <p className="mt-0.5 font-mono text-[10px] text-[var(--color-ink-muted)]">
                    {risk.evidence}
                  </p>
                )}
              </td>
              <td className="px-3 py-2 text-[11px]">
                {risk.projectSlug ? (
                  <Link to={`/projects/${risk.projectSlug}`} className="hover:underline">
                    {risk.projectName}
                  </Link>
                ) : (
                  <span className="text-[var(--color-ink-muted)]">transversal</span>
                )}
              </td>
              <td className="px-3 py-2 text-[11px]">{PROBABILITY_LABEL[risk.probability]}</td>
              <td className="px-3 py-2">
                <Badge
                  tone={
                    risk.impact === 'critical' ? 'bad' : risk.impact === 'high' ? 'warn' : 'neutral'
                  }
                >
                  {IMPACT_LABEL[risk.impact]}
                </Badge>
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <Badge
                  tone={
                    risk.status === 'open' ? 'warn' : risk.status === 'closed' ? 'good' : 'neutral'
                  }
                >
                  {STATUS_LABEL[risk.status]}
                </Badge>
              </td>
              <td className="px-3 py-2 text-[11px] leading-snug text-[var(--color-ink-muted)]">
                {risk.mitigation ?? '—'}
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section title="Bloqueos" subtitle={`${openBlockers.length} sin resolver`}>
        {blockers.length === 0 ? (
          <Empty>Ningún bloqueo registrado.</Empty>
        ) : (
          <Table head={['Bloqueo', 'Proyecto', 'Item', 'Causa', 'Abierto', 'Días', 'Estado']}>
            {blockers.map((blocker) => (
              <tr
                key={blocker.id}
                className={`border-b border-[var(--color-border-subtle)] last:border-0 ${
                  blocker.resolvedAt ? 'opacity-55' : ''
                }`}
              >
                <td className="px-3 py-2">{blocker.title}</td>
                <td className="px-3 py-2 text-[11px]">{blocker.projectSlug ?? '—'}</td>
                <td className="px-3 py-2 text-[11px]">{blocker.itemTitle ?? '—'}</td>
                <td className="px-3 py-2 text-[11px]">{blocker.causeKind ?? '—'}</td>
                <td className="whitespace-nowrap px-3 py-2 text-[11px]">
                  <RelativeTime iso={blocker.openedAt} />
                </td>
                <td className="px-3 py-2 tabular-nums">{blocker.blockedDays}</td>
                <td className="px-3 py-2">
                  {blocker.resolvedAt ? (
                    <Badge tone="good">resuelto</Badge>
                  ) : (
                    <Badge tone="bad">abierto</Badge>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}
